import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EventV4Type } from '../../types/EventType.model';
import {
  InterviewTranscriptionMessageType,
  TranscriptionMessageType,
} from '../../types/Bot.model';

interface PlaybackV4State {
  events: EventV4Type[];
  codeHistory: any;
  userIdMap: any;
  recordingStartTime: number | null;
  tags: { title: string; timestamp: number }[];
  tabs: any;
  activeTab: string;
  isQuestionPanelOpen: boolean;
  transcriptionEvents: TranscriptionMessageType[];
  question?: any;
  staticVideoUrl: string;
  talkingVideoUrl: string;
  transcriptionAudio: { [key: string]: string };
  isAudioPlayback: boolean;
  transcriptionVideo: { [key: string]: string };
  interviewTranscriptionEvents: InterviewTranscriptionMessageType[];
  // AI Assist playback (read-only). Raw nodes as stored under the meeting; the
  // AI playback hook derives the time-synced view-model from these. Empty for
  // interviews without AI Assist activity (tab stays hidden).
  aiConversations: Record<string, any>;
  aiMessages: Record<string, any>;
  aiEditApplied: Record<string, any>;
  aiCodeAuthorship: any[];
}

const initialState: PlaybackV4State = {
  events: [],
  codeHistory: {},
  userIdMap: {},
  recordingStartTime: null,
  tags: [],
  tabs: {},
  activeTab: '',
  isQuestionPanelOpen: false,
  question: null,
  transcriptionEvents: [],
  transcriptionAudio: {},
  transcriptionVideo: {},
  staticVideoUrl: '',
  talkingVideoUrl: '',
  isAudioPlayback: false,
  interviewTranscriptionEvents: [],
  aiConversations: {},
  aiMessages: {},
  aiEditApplied: {},
  aiCodeAuthorship: [],
};

export const playbackV4Slice = createSlice({
  name: 'playbackv4',
  initialState,
  reducers: {
    setTabs: (state, action: PayloadAction<any[]>) => {
      state.tabs = action.payload;
    },
    setEvents: (state, action: PayloadAction<any[]>) => {
      const parsedEvents: EventV4Type[] = [];
      for (const item in action.payload) {
        const parsedItem = { ...action.payload[item] };
        parsedEvents.push(parsedItem);
      }
      const sortedEvents = parsedEvents.sort(
        (item1, item2) => item1.timestamp - item2.timestamp
      );
      state.events = sortedEvents;
    },
    setCodeHistory: (state, action: PayloadAction<any>) => {
      const data = action.payload;
      for (const tab in data) {
        const newCodeHistory: any = [];
        for (const item in data[tab]) {
          newCodeHistory.push(data[tab][item]);
        }
        const sortedEvents = newCodeHistory.sort(
          (item1: any, item2: any) => item1.t - item2.t
        );
        state.codeHistory[tab] = sortedEvents;
      }
    },
    setUserIdMap: (
      state,
      action: PayloadAction<PlaybackV4State['userIdMap']>
    ) => {
      state.userIdMap = action.payload;
    },
    setRecordingStartTime: (
      state,
      action: PayloadAction<PlaybackV4State['recordingStartTime']>
    ) => {
      state.recordingStartTime = action.payload;
    },
    setTags: (state, action: PayloadAction<any>) => {
      const snapshots = action.payload;
      const tags: PlaybackV4State['tags'] = [];
      for (const item in snapshots) {
        tags.push(snapshots[item]);
      }
      state.tags = tags;
    },
    addTags: (state, action: PayloadAction<any>) => {
      const tags: PlaybackV4State['tags'] = action.payload;
      state.tags = [...state.tags, ...tags].sort(
        (a, b) => a.timestamp - b.timestamp
      );
    },
    appendTags: (
      state,
      action: PayloadAction<{ title: string; timestamp: number }>
    ) => {
      state.tags.push(action.payload);
    },
    setIsQuestionPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isQuestionPanelOpen = action.payload;
    },
    setQuestion: (state, action: PayloadAction<any>) => {
      state.question = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      if (state.activeTab !== action.payload) {
        state.activeTab = action.payload;
      }
    },
    setIsAudioPlayback: (state, action: PayloadAction<boolean>) => {
      state.isAudioPlayback = action.payload;
    },
    setStaticVideoUrl: (state, action: PayloadAction<string>) => {
      state.staticVideoUrl = action.payload;
    },
    setTalkingVideoUrl: (state, action: PayloadAction<string>) => {
      state.talkingVideoUrl = action.payload;
    },
    setTranscriptionEvents: (
      state,
      action: PayloadAction<TranscriptionMessageType[]>
    ) => {
      state.transcriptionEvents = action.payload ?? [];
    },
    setInterviewTranscriptionEvents: (
      state,
      action: PayloadAction<InterviewTranscriptionMessageType[]>
    ) => {
      state.interviewTranscriptionEvents = action.payload ?? [];
    },
    setTranscriptionAudio: (
      state,
      action: PayloadAction<{ [key: string]: string }>
    ) => {
      state.transcriptionAudio = action.payload ?? {};
    },
    setTranscriptionVideo: (
      state,
      action: PayloadAction<{ [key: string]: string }>
    ) => {
      state.transcriptionVideo = action.payload ?? {};
    },
    // Load the raw AI Assist nodes for playback. `aiChat` splits into per-agent
    // messages + the conversations metadata; edits/authorship are kept as-is.
    setAiPlaybackData: (
      state,
      action: PayloadAction<{
        aiChat?: Record<string, any> | null;
        aiEditApplied?: Record<string, any> | null;
        aiCodeAuthorship?: Record<string, any> | null;
      }>
    ) => {
      const aiChat = action.payload.aiChat || {};
      const conversations = aiChat.conversations || {};
      const messages: Record<string, any> = {};
      for (const key in aiChat) {
        if (key === 'conversations' || key === 'current') continue;
        messages[key] = aiChat[key]?.messages || {};
      }
      state.aiConversations = conversations;
      state.aiMessages = messages;
      state.aiEditApplied = action.payload.aiEditApplied || {};
      state.aiCodeAuthorship = Object.values(
        action.payload.aiCodeAuthorship || {}
      );
    },
  },
});

export const {
  setActiveTab,
  setEvents,
  setCodeHistory,
  setUserIdMap,
  setRecordingStartTime,
  setInterviewTranscriptionEvents,
  setTranscriptionEvents,
  setTranscriptionAudio,
  setTranscriptionVideo,
  setIsAudioPlayback,
  setQuestion,
  setTags,
  addTags,
  appendTags,
  setTabs,
  setStaticVideoUrl,
  setTalkingVideoUrl,
  setIsQuestionPanelOpen,
  setAiPlaybackData,
} = playbackV4Slice.actions;

export default playbackV4Slice.reducer;
